import { useEffect, useMemo, useRef, useState } from "react";
import {
  amlKycChecks,
  getOnfidoResolutionStatus,
  haveValue,
  isKycAmlDefaultApproved,
} from "../helper/common";
import {
  getCandidacyKycStatus,
  getCandidacyVerificationTenant,
  getDefaultCandidacyChecks,
  needsCandidacyVerification,
} from "../helper/defaultCandidacyChecks";

export default function useDefaultCandidacyChecks(
  tenants,
  getProfileResolutionStatus,
  scopeKey,
  enabled = true,
  debugSource = "candidacy",
  financials = null
) {
  const [resolved, setResolved] = useState(null);
  // Resolution records belong to tenant IDs, not to the profile object's identity.
  const tenantIdsKey = useMemo(
    () =>
      JSON.stringify(
        [
          ...new Set(
            tenants
              .filter((tenant) => needsCandidacyVerification(tenant))
              .map((tenant) => tenant._id)
              .filter(Boolean)
              .map(String)
          ),
        ].sort()
      ),
    [tenants]
  );
  // The context recreates this function on render. It must not trigger a
  // request loop when storing resolution responses updates this component.
  const fetchResolutionsRef = useRef(getProfileResolutionStatus);
  useEffect(() => {
    fetchResolutionsRef.current = getProfileResolutionStatus;
  }, [getProfileResolutionStatus]);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let latestRequest = 0;
    const tenantIds = JSON.parse(tenantIdsKey);
    let inFlight = null;
    const loadResolutions = (force = false) => {
      if (inFlight && !force) return inFlight;
      const requestId = ++latestRequest;
      setResolved((current) => ({
        tenantIdsKey,
        scopeKey,
        pending: true,
        byId:
          current?.tenantIdsKey === tenantIdsKey &&
          current?.scopeKey === scopeKey
            ? current.byId
            : {},
      }));
      console.info(`[CandidacyDecision][${debugSource}] loading resolutions`, {
        scopeKey,
        tenantIds,
      });
      const request = Promise.all(
        tenantIds.map(async (tenantId) => {
          try {
            return [
              tenantId,
              await fetchResolutionsRef.current(tenantId, "kyc"),
            ];
          } catch (error) {
            console.error("Failed to fetch candidacy KYC resolutions", error);
            return [tenantId, null];
          }
        })
      )
        .then((entries) => {
          if (!cancelled && requestId === latestRequest) {
            setResolved({
              tenantIdsKey,
              scopeKey,
              pending: false,
              byId: Object.fromEntries(entries),
            });
          }
        })
        .finally(() => {
          if (requestId === latestRequest) inFlight = null;
        });
      inFlight = request;
      return request;
    };
    const onFocus = () => loadResolutions();
    const onResolutionChange = (event) => {
      if (
        tenantIds.some((id) => String(id) === String(event.detail?.tenantId))
      ) {
        loadResolutions(true);
      }
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") loadResolutions();
    };
    loadResolutions();
    window.addEventListener("focus", onFocus);
    window.addEventListener("skor:kyc-resolution-changed", onResolutionChange);
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
      window.removeEventListener(
        "skor:kyc-resolution-changed",
        onResolutionChange
      );
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [tenantIdsKey, scopeKey, enabled, debugSource]);

  const checks = useMemo(() => {
    const matches =
      enabled &&
      resolved?.tenantIdsKey === tenantIdsKey &&
      resolved?.scopeKey === scopeKey;
    const result = getDefaultCandidacyChecks(
      tenants,
      matches ? resolved.byId : undefined,
      financials
    );
    const isLoading =
      enabled && tenantIdsKey !== "[]" && (!matches || resolved.pending);
    // A definitive default/effort rejection does not depend on KYC resolutions.
    const waitingForDecision = isLoading && result.status !== "declined";
    return {
      ...result,
      isLoading,
      status: waitingForDecision ? "loading" : result.status,
      titleKey: waitingForDecision ? "calculating" : result.titleKey,
    };
  }, [tenants, resolved, tenantIdsKey, scopeKey, enabled, financials]);

  useEffect(() => {
    const resolutionsLoaded =
      enabled &&
      resolved?.tenantIdsKey === tenantIdsKey &&
      resolved?.scopeKey === scopeKey &&
      !resolved.pending;
    const byId = resolutionsLoaded ? resolved.byId : {};
    console.info(`[CandidacyDecision][${debugSource}] decision`, {
      scopeKey,
      enabled,
      status: enabled ? checks.status : "default_rules_disabled",
      resolutionsLoaded,
      isLoading: checks.isLoading,
      tenantCount: tenants.length,
      totalIncome: checks.totalIncome,
      effortRate: checks.effortRate,
      effortRateExceeded: checks.effortRateExceeded,
      defaulterIds: checks.defaulters.map((tenant) => tenant._id),
      manualReviewIds: checks.pendingVerification.map((tenant) => tenant._id),
      tenants: tenants.map((tenant) => {
        const kyc = tenant?.onfido_user_kyc;
        const workflow = kyc?.onfido_workflow_res;
        const workflowStatus = workflow?.object?.status ?? workflow?.status;
        const verificationTenant = getCandidacyVerificationTenant(tenant);
        const verificationWorkflow =
          verificationTenant?.onfido_user_kyc?.onfido_workflow_res;
        const output =
          verificationWorkflow?.output ??
          verificationWorkflow?.resource?.output;
        const resolutionStatus = getOnfidoResolutionStatus(
          output,
          byId[tenant._id]
        );
        const hasWorkflowStatus = haveValue(workflowStatus);
        const effectiveKycStatus = getCandidacyKycStatus(tenant);
        const hasVerificationStatus =
          haveValue(effectiveKycStatus) && effectiveKycStatus !== "not_started";
        const defaultApproved = isKycAmlDefaultApproved(verificationTenant);
        const approvedWithResolutions = amlKycChecks(
          verificationTenant,
          byId[tenant._id]
        );
        return {
          tenantId: tenant._id,
          hasKycRecord: Boolean(kyc),
          hasWorkflowResponse: Boolean(workflow),
          workflowStatus: workflowStatus ?? null,
          resourceStatus: workflow?.resource?.status ?? null,
          kycStatus: kyc?.kyc_status ?? null,
          kycProvider: kyc?.kyc_provider ?? null,
          amlResult: output?.aml_report?.result ?? null,
          normalizedAmlResult: kyc?.kyc_aml?.result ?? null,
          hasWorkflowStatus,
          effectiveKycStatus: effectiveKycStatus ?? null,
          hasVerificationStatus,
          defaultApproved,
          approvedWithResolutions,
          resolutionRequestFailed:
            resolutionsLoaded && byId[tenant._id] === null,
          unclearCategoryKeys: resolutionStatus.categoryResults.nonClear.map(
            (category) => category.key
          ),
          resolvedCategoryKeys: [...resolutionStatus.resolvedCategoryKeys],
          verificationReason: !hasVerificationStatus
            ? "verification_not_started_or_status_missing"
            : defaultApproved
              ? "kyc_approved_and_aml_clear"
              : approvedWithResolutions
                ? "unclear_categories_manually_resolved"
                : "requires_manual_review",
        };
      }),
    });
  }, [checks, tenants, resolved, tenantIdsKey, scopeKey, enabled, debugSource]);

  return checks;
}
